import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

const DEADLINE = new Date("2026-09-07T23:59:00+02:00");

function useCountdown() {
  const calc = () => {
    const diff = DEADLINE - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };

  const [time, setTime] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, []);

  return time;
}

function Digit({ value, label, compact }) {
  const str = String(value).padStart(2, "0");

  return (
    <div className="flex flex-col items-center">
      <div
        className={`flex items-center justify-center overflow-hidden rounded-lg ${
          compact ? "w-10 h-9" : "w-9 h-9 sm:w-10 sm:h-10"
        }`}
        style={{
          background: compact ? "rgba(201,168,76,0.08)" : "rgba(201,168,76,0.06)",
          border: "1px solid rgba(201,168,76,0.18)",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={str}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`font-bold font-mono text-[#C9A84C] leading-none tabular-nums ${
              compact ? "text-base" : "text-sm sm:text-base"
            }`}
            style={{ display: "block", textShadow: compact ? "none" : "0 0 12px rgba(201,168,76,0.35)" }}
          >
            {str}
          </motion.span>
        </AnimatePresence>
      </div>
      <span
        className={`uppercase tracking-widest mt-1 font-medium text-[#86868b] ${
          compact ? "text-[8px]" : "text-[8px]"
        }`}
      >
        {compact ? label.slice(0, 3) : label}
      </span>
    </div>
  );
}

export default function CountdownTimer({ compact = false }) {
  const { days, hours, minutes, seconds } = useCountdown();
  const sep = (
    <span className={`text-[#C9A84C]/40 font-bold leading-none ${compact ? "text-base mb-3" : "text-sm mb-3"}`}>
      :
    </span>
  );

  return (
    <div className={`flex items-center justify-center ${compact ? "gap-1.5" : "gap-1.5"}`} data-testid="countdown-timer">
      <Digit value={days} label="Tage" compact={compact} />
      {sep}
      <Digit value={hours} label="Stunden" compact={compact} />
      {sep}
      <Digit value={minutes} label="Minuten" compact={compact} />
      {sep}
      <Digit value={seconds} label="Sekunden" compact={compact} />
    </div>
  );
}
